// Core
import { sessionUtils } from '@openbank/cf-ui-architecture-core';
// Constants
import {
  CALLBACK_URL_QUERY_PARAM,
  LANGUAGE_QUERY_PARAM,
  TOKEN_QUERY_PARAM,
} from './constants/token.handlers.constants';
import { ONBOARDING_INIT_LOCAL_STORAGE } from 'constants/app.constants';
// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppInformationNeedEnumeration } from 'enumerations/app-information-need.enumeration';
// Resolvers
import { fetchCustomerAuthorizationDataResolver } from '../resolvers/token.resolver';
// Types
import {
  TokenBuilderHandlersType,
  TokenMethodsHandlersType,
  TokenSaveAuthDataAndCleanUrlType,
} from './types/token.handlers.type';
// Utilities
import {
  isQueryParamTokenOrAccessToken,
  translationsHasBeenLoaded,
} from './utils/token.handlers.utils';
import { getAppDomain } from 'utils/domain.utils';

const updateTokenInformationFlag =
  () =>
  (prevFlags: AppInformationNeedEnumeration[]): AppInformationNeedEnumeration[] =>
    [...prevFlags, AppInformationNeedEnumeration.AUTHORIZATION_DATA];

const saveAuthDataAndCleanUrl = ({
  callbackUrl,
  language,
  response,
  setInformationFlags,
}: TokenSaveAuthDataAndCleanUrlType): void => {
  const url = `${window.location.origin}${window.location.pathname}?${CALLBACK_URL_QUERY_PARAM}=${callbackUrl}&${LANGUAGE_QUERY_PARAM}=${language}`;

  sessionUtils.saveSessionData({ ...response, domain: getAppDomain(), secure: true });
  window.history.pushState({}, '', url);
  setInformationFlags(updateTokenInformationFlag());

  if (!localStorage.getItem(ONBOARDING_INIT_LOCAL_STORAGE)) {
    localStorage.setItem(ONBOARDING_INIT_LOCAL_STORAGE, new Date().toString());
  }
};

const cleanAuthDataAndThrowException = ({
  setError,
}: Pick<TokenBuilderHandlersType, 'setError'>): void => {
  sessionUtils.clearSessionData();
  setError(AppErrorEnumeration.CUSTOMER_AUTHORIZATION_FAILED);
};

// eslint-disable-next-line complexity
const fetchCustomerAuthorizationDataHandler = async ({
  informationFlags,
  urlSearchParams,
  setInformationFlags,
  setError,
}: TokenBuilderHandlersType): Promise<void> => {
  const token = urlSearchParams.get(TOKEN_QUERY_PARAM) || '';
  if (translationsHasBeenLoaded(informationFlags)) {
    if (!sessionUtils.isUserLogged() || token) {
      const callbackUrl = urlSearchParams.get(CALLBACK_URL_QUERY_PARAM) || '';
      const language = urlSearchParams.get(LANGUAGE_QUERY_PARAM) || '';
      if (isQueryParamTokenOrAccessToken(token)) {
        await sessionUtils.clearSessionData();
        const [response] = await fetchCustomerAuthorizationDataResolver(token);

        response
          ? saveAuthDataAndCleanUrl({ callbackUrl, language, response, setInformationFlags })
          : cleanAuthDataAndThrowException({ setError });
      } else {
        sessionUtils.clearSessionData();
        setError(AppErrorEnumeration.NO_ONE_TOKEN);
      }
    } else {
      setInformationFlags(updateTokenInformationFlag());
    }
  }
};

const TokenHandlers = (props: TokenBuilderHandlersType): TokenMethodsHandlersType => ({
  handleFetchCustomerAuthorizationData: () => fetchCustomerAuthorizationDataHandler(props),
});

export { TokenHandlers };
